// Common
import { NonScCommon } from './nonsc.common'

import {
  NonScHomeCommon as HomeCommon
} from './nonsc-home.common'

import {
  NonScUserCommon as UserCommon
} from './nonsc-user.common'

// Utils
import {
  ValidateUtility as Validate
} from '../utils/validate'

import {
  sanitize, isValidNewInput, invalidReason, comparisonNormalize
} from '../utils/phone'

/**
 * Import item properties
 * @typedef ImportRecord
 * @type {Object}
 * @property {Number} id - Record ID
 * @typedef {HomeInput & UserInput & ImportRecord} ImportItem
 */

/**
 * Import item valid state
 * @typedef ImportItemValid
 * @type {Object}
 * @property {Boolean} all                      - Valid all
 * @property {Boolean | String} name            - Valid name
 * @property {Boolean | String} mobileNoValid   - Valid mobile number
 * @property {String | null} mobileNoReason     - Reason mobileNoValid is false, or null when valid
 * @property {Boolean | String} mobileNoUnique  - Valid mobile number
 * @property {Boolean | String} emailValid      - Valid email
 * @property {Boolean | String} emailUnique     - Valid email
 * @property {Boolean | String} address         - Valid address
 * @property {Boolean | String} structure       - Valid construction type
 * @property {Boolean | String} area            - Valid usable area
 * @property {Boolean | String} role            - Valid role
 */

export class NonScImportCommon {

  /** @type {ImportItemValid} */
  static valid = {
    all             : false,
    name            : false,
    mobileNoValid   : false,
    mobileNoReason  : null,
    mobileNoUnique  : false,
    emailValid      : true,
    emailUnique     : true,
    address         : false,
    structure       : false,
    area            : false,
    role            : false,
    transferDate    : false,
    unique          : false,
  }

  /** @type {ImportItemValid} */
  static error = {
    name            : 'ชื่อนามสกุลไม่ถูกต้อง',
    mobileNoValid   : 'เบอร์มือถือไม่ถูกต้อง',
    mobileNoUnique  : 'เบอร์มือถือซ้ำ',
    emailValid      : 'อีเมล์ไม่ถูกต้อง',
    emailUnique     : 'อีเมล์ซ้ำ',
    address         : 'ที่อยู่ไม่ถูกต้อง',
    structure       : 'ประเภทสิ่งก่อสร้างไม่ถูกต้อง',
    area            : 'พื้นที่ใช้สอยไม่ถูกต้อง',
    role            : 'สถานะไม่ถูกต้อง',
    transferDate    : 'วันที่โอนไม่ถูกต้อง',
    unique          : 'ข้อมูลซ้ำ',
  }

  /** @type {ImportItem} */
  static item = ({
    id: null, ...UserCommon.input, ...HomeCommon.input
  })

  /**
   * Import input validator
   * @param {ImportItem} item - Import Data
   * @returns {ImportItemValid} Import Data
   */
  static validator(item) {
  
    // Valid State
    /** @type {ImportItemValid} */
    const valid = {
      all             : false,
      name            : false,
      mobileNoUnique  : false,
      mobileNoValid   : false,
      mobileNoReason  : null,
      emailUnique     : true,
      emailValid      : true,
      address         : false,
      structure       : false,
      role            : false,
      transferDate    : false,
      unique          : false
    }

    // Validate Item
    if (!item) return valid

    // Item Props
    const {
      firstName,
      lastName,
      role,
      addressNo,
      structure,
      mobileNo,
      transferDate,
    } = item

    // Validate Mobile No
    valid.mobileNoReason = invalidReason(mobileNo)
    valid.mobileNoValid = (
      valid.mobileNoReason === null
    )

    // Validate Name
    valid.name = (
      !!firstName && !!lastName && Validate.isName(firstName) && Validate.isName(lastName)
    )

    // Validate Role
    valid.role = Validate.isRole(role)

    // Validate Address
    valid.address = (
      Validate.isLocation(item) && Validate.isAddrNo(addressNo)
    )

    // Validate Construct
    const building = NonScCommon.construct.find(
      ({ name }) => name === structure
    )

    // Validate Structure
    if (building) {
      valid.structure = (
        Validate.isStructure(building.value)
      )
    }

    // Validate Transfer Date: an empty cell stays allowed, since the import
    // endpoint does not store the date at all
    valid.transferDate = (
      Validate.isOptionalDate(transferDate)
    )

    return valid
  }

  /**
   * Compare a row value against the rest of the batch
   * @param {ImportItem[]} rows - Import Data List
   * @param {Number} index      - Import Index
   * @param {ImportItem} input  - Import Data
   * @param {String} key        - Compare Key
   * @returns {Boolean} Unique State
   */
  static isUniqueBy(rows, index, input, key) {

    const read = (e) => {
      const value = e[key]

      // NOTE: an invalid value stays raw, so two unrelated garbage rows never compare equal.
      if (key === 'mobileNo') {
        return isValidNewInput(value) ? comparisonNormalize(value) : value
      }

      return value
    }

    const unique = (e) => `${read(e)}`.concat(e.firstName, e.lastName)

    const compare = (e) => unique(e) !== unique(input)

    const source = rows
      .filter((_, i) => i !== index)
      .filter(compare)
      .map(read)

    const target = `${read(input)}`

    return source.filter((e) => !!e).every((e) => `${e}` !== target)
  }

  /**
   * Map & Format Import Data
   * @param {ImportItem} item  - Import Data
   * @param {Number} index          - Import Index
   * @returns {ImportItem} Import Data
   */
  static map(item, index) {

    // Validate Item
    if (!item) return item

    // Item Props
    const {
      firstName,
      lastName,
      mobileNo,
      zipCode,
      transferDate,
      role: itemRole,
      ...rest
    } = item

    const role = NonScCommon.role.find(
      e => e.value === String(itemRole || '').toUpperCase()
    )

    const isDate = /\d{1,2}\/\d{1,2}\/\d{4}/g
    let formattedTransferDate = transferDate

    if (isDate.test(transferDate)) {
      const [d, m, y] = transferDate.split('/')
      formattedTransferDate = `${y}-${m}-${d}`
    }

    const mapped = {
      ...rest,
      firstName,
      lastName,
      id: index + 1,
      mobileNo: mobileNo ? sanitize(mobileNo) : mobileNo,
      roleName: role ? role.name : null,
      role: role ? role.value : null,
      zipCode: `${zipCode}`,
      transferDate: formattedTransferDate,
    }

    if (firstName && lastName) {
      mapped.name = [firstName, lastName].join(' ')
    }

    return mapped
  }
}
